import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Navigation } from "./Dashboard";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../App";
import { 
  User, 
  Mail, 
  Calendar,
  Trophy,
  Flame,
  Star,
  BookOpen,
  Brain,
  Award,
  ChevronRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [quizHistory, setQuizHistory] = useState([]);

  useEffect(() => {
    // Could fetch quiz history here if endpoint exists
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const levelProgress = {
    débutant: { min: 0, max: 50, next: "intermédiaire" },
    intermédiaire: { min: 50, max: 150, next: "avancé" },
    avancé: { min: 150, max: 500, next: "expert" },
    expert: { min: 500, max: 1000, next: null }
  };

  const currentLevel = user?.level || "débutant";
  const levelInfo = levelProgress[currentLevel] || levelProgress.débutant;
  const wordsLearned = user?.words_learned || 0;
  const progressPercent = Math.min(((wordsLearned - levelInfo.min) / (levelInfo.max - levelInfo.min)) * 100, 100);

  const stats = [
    { icon: Flame, label: "Streak actuel", value: user?.streak || 0, suffix: "jours", color: "text-orange-500" },
    { icon: Star, label: "Points totaux", value: user?.points || 0, suffix: "pts", color: "text-primary" },
    { icon: BookOpen, label: "Mots appris", value: user?.words_learned || 0, suffix: "", color: "text-accent" },
    { icon: Brain, label: "Quiz complétés", value: user?.quizzes_completed || 0, suffix: "", color: "text-secondary" },
  ];

  const badgeDescriptions = {
    nouveau: { label: "Nouveau membre", description: "Bienvenue dans la communauté !" },
    premier_quiz: { label: "Premier Quiz", description: "Vous avez complété votre premier quiz" },
    quiz_master: { label: "Quiz Master", description: "10 quiz complétés" },
    parfait: { label: "Score Parfait", description: "Score parfait sur un quiz" },
    contributeur: { label: "Contributeur", description: "Première contribution au dictionnaire" },
    streak_7: { label: "Semaine de feu", description: "7 jours consécutifs" },
    streak_30: { label: "Mois de feu", description: "30 jours consécutifs" },
  };

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <Navigation />
      
      <main className="pt-20 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Profile Header */}
          <Card className="border-border/50 mb-8 overflow-hidden animate-slide-up">
            <div className="h-24 bg-gradient-to-r from-primary/20 to-accent/20 baoule-pattern"></div>
            <CardContent className="p-6 -mt-12">
              <div className="flex flex-col sm:flex-row items-center sm:items-end gap-4">
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-24 h-24 rounded-2xl border-4 border-background shadow-lg"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-2xl bg-primary/10 border-4 border-background shadow-lg flex items-center justify-center">
                    <User className="w-12 h-12 text-primary" />
                  </div>
                )}
                
                <div className="flex-1 text-center sm:text-left">
                  <h1 className="font-heading text-2xl font-bold">{user?.name}</h1>
                  <div className="flex items-center justify-center sm:justify-start gap-2 text-muted-foreground mt-1">
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">{user?.email}</span>
                  </div>
                </div>

                <div className="text-center sm:text-right">
                  <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary font-medium capitalize">
                    Niveau: {currentLevel}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 animate-slide-up stagger-1">
            {stats.map((stat) => (
              <Card key={stat.label} className="border-border/50">
                <CardContent className="p-4 text-center">
                  <stat.icon className={`w-8 h-8 mx-auto mb-2 ${stat.color}`} />
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Level Progress */}
            <Card className="border-border/50 animate-slide-up stagger-2">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-primary" />
                  Progression
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="capitalize">{currentLevel}</span>
                    {levelInfo.next && (
                      <span className="text-muted-foreground capitalize">{levelInfo.next}</span>
                    )}
                  </div>
                  <Progress value={progressPercent} className="h-3" />
                  <p className="text-xs text-muted-foreground mt-2">
                    {wordsLearned} / {levelInfo.max} mots pour passer au niveau suivant
                  </p>
                </div>

                <div className="pt-4 border-t border-border">
                  <h4 className="font-medium mb-3">Objectifs</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                      <span className="text-sm">Apprendre 10 nouveaux mots</span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                      <span className="text-sm">Compléter un quiz parfait</span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Badges */}
            <Card className="border-border/50 animate-slide-up stagger-3">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary" />
                  Badges ({user?.badges?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {user?.badges?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {user.badges.map((badge) => {
                      const badgeInfo = badgeDescriptions[badge] || { label: badge, description: "" };
                      return (
                        <div 
                          key={badge}
                          className="p-3 rounded-xl bg-primary/5 border border-primary/10 hover:bg-primary/10 transition-colors"
                        >
                          <p className="font-medium text-sm">{badgeInfo.label}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {badgeInfo.description}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Award className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">
                      Aucun badge pour l'instant
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Complétez des quiz et contribuez pour gagner des badges !
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Account Info */}
          <Card className="border-border/50 mt-6 animate-slide-up stagger-4">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Informations du compte</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Membre depuis</p>
                    <p className="text-xs text-muted-foreground">
                      {user?.created_at 
                        ? new Date(user.created_at).toLocaleDateString('fr-FR', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })
                        : "Date inconnue"
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-border">
                <Button 
                  variant="destructive" 
                  onClick={handleLogout}
                  className="rounded-full"
                >
                  Se déconnecter
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
